import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Calendar, Package, Users, FileText, Award, Building, Gavel,
  Plus, Eye, CheckCircle, XCircle, Clock, AlertCircle,
  ChevronRight, Edit, Trash2, RefreshCw, UserPlus, Lock,
  BarChart3, TrendingUp, Mail, Phone, Globe, ImagePlus, Upload, X
} from 'lucide-react';

export default function AdminEncheres() {
  const { API } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState('vacations');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Data states
  const [vacations, setVacations] = useState([]);
  const [lots, setLots] = useState([]);
  const [inscriptions, setInscriptions] = useState([]);
  const [demandesVendeur, setDemandesVendeur] = useState([]);
  const [comptes, setComptes] = useState({ acheteurs: [], vendeurs: [] });
  const [attributions, setAttributions] = useState([]);

  // Modal states
  const [showVacationModal, setShowVacationModal] = useState(false);
  const [editingVacation, setEditingVacation] = useState(null);
  const [showInscriptionModal, setShowInscriptionModal] = useState(false);
  const [selectedInscription, setSelectedInscription] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Lot modal states
  const [showLotModal, setShowLotModal] = useState(false);
  const [editingLot, setEditingLot] = useState(null);
  const [lotPhotos, setLotPhotos] = useState([]);
  const [uploadingLotPhoto, setUploadingLotPhoto] = useState(false);

  // Filter states
  const [vacationFilter, setVacationFilter] = useState(null);
  const [inscriptionStatusFilter, setInscriptionStatusFilter] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, vacRes, lotsRes, inscRes, demandesRes, acheteursRes, vendeursRes, attrRes] = await Promise.all([
        axios.get(`${API}/encheres/admin/stats`),
        axios.get(`${API}/encheres/admin/vacations`),
        axios.get(`${API}/encheres/admin/lots`),
        axios.get(`${API}/encheres/admin/inscriptions`),
        axios.get(`${API}/encheres/admin/demandes-vendeur`),
        axios.get(`${API}/encheres/admin/comptes/acheteurs`),
        axios.get(`${API}/encheres/admin/comptes/vendeurs`),
        axios.get(`${API}/encheres/admin/attributions`)
      ]);
      
      setStats(statsRes.data);
      setVacations(vacRes.data || []);
      setLots(lotsRes.data || []);
      setInscriptions(inscRes.data || []);
      setDemandesVendeur(demandesRes.data || []);
      setComptes({ acheteurs: acheteursRes.data || [], vendeurs: vendeursRes.data || [] });
      setAttributions(attrRes.data || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Vacation CRUD
  const [vacationForm, setVacationForm] = useState({
    titre_fr: '',
    description_fr: '',
    commission_pct: 10,
    frais_dossier_eur: 25,
    date_cloture_inscriptions: '',
    date_ouverture_vente: '',
    date_cloture_vente: ''
  });

  // Lot form
  const [lotForm, setLotForm] = useState({
    vacation_id: '',
    titre: '',
    description_vendeur_fr: '',
    marque: '',
    modele: '',
    annee: '',
    heures_utilisation: '',
    etat_general: 'bon',
    localisation_fr: '',
    commune: '',
    departement: '',
    mise_a_prix: 0,
    pas_enchere: 100
  });

  const handleSaveVacation = async () => {
    try {
      let vacationId;
      if (editingVacation) {
        await axios.put(`${API}/encheres/admin/vacations/${editingVacation.id}`, vacationForm);
        vacationId = editingVacation.id;
        
        // Delete image if it was removed (had image before, now null)
        if (editingVacation.id && !editingVacation.image_url && editingVacation._originalImageUrl) {
          await axios.delete(`${API}/encheres/admin/vacations/${editingVacation.id}/image`);
        }
        
        toast.success('Vacation mise à jour');
      } else {
        const response = await axios.post(`${API}/encheres/admin/vacations`, vacationForm);
        vacationId = response.data.vacation_id;
        toast.success('Vacation créée');
      }
      
      // Upload image if selected
      if (imagePreview && imagePreview.file) {
        await handleUploadVacationImage(vacationId, imagePreview.file);
      }
      
      setShowVacationModal(false);
      setEditingVacation(null);
      setImagePreview(null);
      setVacationForm({
        titre_fr: '', description_fr: '', commission_pct: 10, frais_dossier_eur: 25,
        date_cloture_inscriptions: '', date_ouverture_vente: '', date_cloture_vente: ''
      });
      fetchAllData();
    } catch (error) {
      let msg = 'Erreur';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        msg = detail.msg || detail.message || JSON.stringify(detail);
      }
      toast.error(msg);
    }
  };

  const handleUploadVacationImage = async (vacationId, file) => {
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/encheres/admin/vacations/${vacationId}/upload-image`, formData);
      toast.success('Photo de couverture uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Format non supporté. Utilisez PNG, JPG, GIF ou WEBP.');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 5 Mo)');
        return;
      }
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview({ url: reader.result, file });
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePublishVacation = async (vacationId) => {
    try {
      await axios.post(`${API}/encheres/admin/vacations/${vacationId}/publish`);
      toast.success('Vacation publiée');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }
  };

  const handleUpdateVacationStatus = async (vacationId, newStatus) => {
    try {
      await axios.post(`${API}/encheres/admin/vacations/${vacationId}/update-status?new_status=${newStatus}`);
      toast.success('Statut mis à jour');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Inscription actions
  const handleValidateInscription = async (inscriptionId) => {
    try {
      await axios.post(`${API}/encheres/admin/inscriptions/${inscriptionId}/validate`);
      toast.success('Inscription validée');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleRefuseInscription = async (inscriptionId) => {
    try {
      await axios.post(`${API}/encheres/admin/inscriptions/${inscriptionId}/refuse`);
      toast.success('Inscription refusée');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Demande vendeur actions
  const handleCreateVendorAccount = async (demandeId) => {
    try {
      await axios.post(`${API}/encheres/admin/demandes-vendeur/${demandeId}/creer-compte`);
      toast.success('Compte vendeur créé ! Un email avec le mot de passe a été envoyé.');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  // Account actions
  const handleToggleAccount = async (userId, action) => {
    try {
      await axios.post(`${API}/encheres/admin/comptes/${userId}/${action}`);
      toast.success(action === 'activer' ? 'Compte activé' : 'Compte désactivé');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      await axios.post(`${API}/encheres/admin/comptes/${userId}/reset-password`);
      toast.success('Nouveau mot de passe envoyé par email');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Lot CRUD
  const handleSaveLot = async () => {
    try {
      let lotId;
      if (editingLot) {
        await axios.put(`${API}/encheres/admin/lots/${editingLot.id}`, lotForm);
        lotId = editingLot.id;
        toast.success('Lot mis à jour');
      } else {
        const response = await axios.post(`${API}/encheres/admin/lots`, lotForm);
        lotId = response.data.lot_id;
        toast.success('Lot créé');
      }
      
      setShowLotModal(false);
      setEditingLot(null);
      setLotPhotos([]);
      setLotForm({
        vacation_id: '', titre: '', description_vendeur_fr: '', marque: '', modele: '',
        annee: '', heures_utilisation: '', etat_general: 'bon', localisation_fr: '',
        commune: '', departement: '', mise_a_prix: 0, pas_enchere: 100
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleLotPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !editingLot) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez PNG, JPG, GIF ou WEBP.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return;
    }

    setUploadingLotPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/encheres/admin/lots/${editingLot.id}/upload-photo`, formData);
      toast.success('Photo uploadée');
      
      // Refresh lot data
      const lotRes = await axios.get(`${API}/encheres/admin/lots`);
      const updatedLot = lotRes.data.find(l => l.id === editingLot.id);
      if (updatedLot) {
        setLotPhotos(updatedLot.photos || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploadingLotPhoto(false);
    }
  };

  const handleDeleteLotPhoto = async (photoIndex) => {
    if (!editingLot) return;
    
    try {
      await axios.delete(`${API}/encheres/admin/lots/${editingLot.id}/photos/${photoIndex}`);
      toast.success('Photo supprimée');
      setLotPhotos(prev => prev.filter((_, i) => i !== photoIndex));
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditLotModal = (lot) => {
    setEditingLot(lot);
    setLotForm({
      vacation_id: lot.vacation_id || '',
      titre: lot.titre || lot.titre_fr || '',
      description_vendeur_fr: lot.description_vendeur_fr || '',
      marque: lot.marque || '',
      modele: lot.modele || '',
      annee: lot.annee || '',
      heures_utilisation: lot.heures_utilisation || '',
      etat_general: lot.etat_general || 'bon',
      localisation_fr: lot.localisation_fr || '',
      commune: lot.commune || '',
      departement: lot.departement || '',
      mise_a_prix: lot.mise_a_prix || 0,
      pas_enchere: lot.pas_enchere || 100
    });
    setLotPhotos(lot.photos || []);
    setShowLotModal(true);
  };

  const openNewLotModal = () => {
    setEditingLot(null);
    setLotForm({
      vacation_id: '', titre: '', description_vendeur_fr: '', marque: '', modele: '',
      annee: '', heures_utilisation: '', etat_general: 'bon', localisation_fr: '',
      commune: '', departement: '', mise_a_prix: 0, pas_enchere: 100
    });
    setLotPhotos([]);
    setShowLotModal(true);
  };

  const getStatusBadge = (statut, type = 'vacation') => {
    const configs = {
      vacation: {
        brouillon: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700' },
        annoncee: { label: 'Annoncée', color: 'bg-green-100 text-green-700' },
        inscriptions_closes: { label: 'Inscr. closes', color: 'bg-yellow-100 text-yellow-700' },
        vente_en_cours: { label: 'Vente en cours', color: 'bg-[#F0A22E]/20 text-[#F0A22E]' },
        close: { label: 'Close', color: 'bg-blue-100 text-blue-700' },
        attribuee: { label: 'Attribuée', color: 'bg-emerald-100 text-emerald-700' }
      },
      inscription: {
        recue: { label: 'Reçue', color: 'bg-blue-100 text-blue-700' },
        incomplete: { label: 'Incomplète', color: 'bg-yellow-100 text-yellow-700' },
        validee: { label: 'Validée', color: 'bg-green-100 text-green-700' },
        refusee: { label: 'Refusée', color: 'bg-red-100 text-red-700' }
      },
      demande: {
        recue: { label: 'Reçue', color: 'bg-blue-100 text-blue-700' },
        qualifiee: { label: 'Qualifiée', color: 'bg-yellow-100 text-yellow-700' },
        refusee: { label: 'Refusée', color: 'bg-red-100 text-red-700' },
        compte_cree: { label: 'Compte créé', color: 'bg-green-100 text-green-700' }
      }
    };
    const config = configs[type]?.[statut] || { label: statut, color: 'bg-gray-100 text-gray-700' };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatPrice = (price) => new Intl.NumberFormat('fr-FR').format(price || 0);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex-1 flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#F0A22E]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Helmet>
        <title>Gestion Enchères | Admin GEOTS</title>
      </Helmet>

      <div className="p-6 overflow-auto" data-testid="admin-encheres-content">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2540]" data-testid="admin-encheres-title">Gestion des Enchères</h1>
            <p className="text-gray-500">Vente par courtage aux enchères</p>
          </div>
          <Button onClick={fetchAllData} variant="outline" data-testid="admin-encheres-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6" data-testid="admin-encheres-stats">
            <Card data-testid="stat-ventes-actives">
              <CardContent className="p-4 text-center">
                <Calendar className="w-6 h-6 mx-auto text-[#0A2540] mb-1" />
                <p className="text-xl font-bold" data-testid="stat-ventes-actives-value">{(stats.vacations?.annoncee || 0) + (stats.vacations?.vente_en_cours || 0)}</p>
                <p className="text-xs text-gray-500">Ventes actives</p>
              </CardContent>
            </Card>
            <Card data-testid="stat-lots">
              <CardContent className="p-4 text-center">
                <Package className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                <p className="text-xl font-bold" data-testid="stat-lots-value">{stats.total_lots}</p>
                <p className="text-xs text-gray-500">Lots</p>
              </CardContent>
            </Card>
            <Card data-testid="stat-inscrits">
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 mx-auto text-green-600 mb-1" />
                <p className="text-xl font-bold" data-testid="stat-inscrits-value">{stats.inscriptions_validated}</p>
                <p className="text-xs text-gray-500">Inscrits validés</p>
              </CardContent>
            </Card>
            <Card className="bg-orange-50" data-testid="stat-inscr-pending">
              <CardContent className="p-4 text-center">
                <AlertCircle className="w-6 h-6 mx-auto text-orange-600 mb-1" />
                <p className="text-xl font-bold text-orange-600" data-testid="stat-inscr-pending-value">{stats.inscriptions_pending}</p>
                <p className="text-xs text-gray-500">Inscr. en attente</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50" data-testid="stat-demandes-vendeur">
              <CardContent className="p-4 text-center">
                <Building className="w-6 h-6 mx-auto text-purple-600 mb-1" />
                <p className="text-xl font-bold text-purple-600" data-testid="stat-demandes-vendeur-value">{stats.demandes_vendeur_pending}</p>
                <p className="text-xs text-gray-500">Dem. vendeurs</p>
              </CardContent>
            </Card>
            <Card data-testid="stat-offres">
              <CardContent className="p-4 text-center">
                <Gavel className="w-6 h-6 mx-auto text-[#F0A22E] mb-1" />
                <p className="text-xl font-bold" data-testid="stat-offres-value">{stats.total_offres}</p>
                <p className="text-xs text-gray-500">Offres</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="admin-encheres-tabs">
          <TabsList className="bg-white border shadow-sm mb-6" data-testid="admin-encheres-tabs-list">
            <TabsTrigger value="vacations" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-vacations">
              <Calendar className="w-4 h-4 mr-2" />
              Vacations
            </TabsTrigger>
            <TabsTrigger value="lots" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-lots">
              <Package className="w-4 h-4 mr-2" />
              Lots
            </TabsTrigger>
            <TabsTrigger value="inscriptions" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-inscriptions">
              <Users className="w-4 h-4 mr-2" />
              Inscriptions
              {stats?.inscriptions_pending > 0 && (
                <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {stats.inscriptions_pending}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="demandes" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-demandes">
              <FileText className="w-4 h-4 mr-2" />
              Dem. Vendeurs
              {stats?.demandes_vendeur_pending > 0 && (
                <span className="ml-2 bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {stats.demandes_vendeur_pending}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="comptes" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-comptes">
              <Building className="w-4 h-4 mr-2" />
              Comptes
            </TabsTrigger>
            <TabsTrigger value="attributions" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-attributions">
              <Award className="w-4 h-4 mr-2" />
              Attributions
            </TabsTrigger>
          </TabsList>

          {/* VACATIONS TAB */}
          <TabsContent value="vacations" className="space-y-4" data-testid="vacations-tab-content">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Gestion des vacations</h2>
              <Button onClick={() => { setEditingVacation(null); setShowVacationModal(true); }} data-testid="btn-new-vacation">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle vacation
              </Button>
            </div>

            <div className="space-y-3">
              {vacations.map(vacation => (
                <Card key={vacation.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusBadge(vacation.statut, 'vacation')}
                          <span className="text-sm text-gray-500">{vacation.nb_lots} lots • {vacation.nb_inscriptions} inscr.</span>
                        </div>
                        <h3 className="font-bold text-[#0A2540]">{vacation.titre_fr}</h3>
                        <p className="text-sm text-gray-500 mt-1">{vacation.description_fr}</p>
                        <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
                          <span>Clôture inscr.: {formatDate(vacation.date_cloture_inscriptions)}</span>
                          <span>Ouverture: {formatDate(vacation.date_ouverture_vente)}</span>
                          <span>Clôture: {formatDate(vacation.date_cloture_vente)}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {vacation.statut === 'brouillon' && (
                          <Button size="sm" onClick={() => handlePublishVacation(vacation.id)}>
                            Publier
                          </Button>
                        )}
                        {vacation.statut === 'annoncee' && (
                          <Select onValueChange={(v) => handleUpdateVacationStatus(vacation.id, v)}>
                            <SelectTrigger className="w-40">
                              <SelectValue placeholder="Changer statut" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="inscriptions_closes">Clore inscriptions</SelectItem>
                              <SelectItem value="vente_en_cours">Ouvrir vente</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        {vacation.statut === 'vente_en_cours' && (
                          <Button size="sm" variant="outline" onClick={() => handleUpdateVacationStatus(vacation.id, 'close')}>
                            Clore la vente
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingVacation({...vacation, _originalImageUrl: vacation.image_url});
                            setVacationForm({
                              titre_fr: vacation.titre_fr,
                              description_fr: vacation.description_fr || '',
                              commission_pct: vacation.commission_pct,
                              frais_dossier_eur: vacation.frais_dossier_eur,
                              date_cloture_inscriptions: vacation.date_cloture_inscriptions?.split('T')[0] || '',
                              date_ouverture_vente: vacation.date_ouverture_vente?.split('T')[0] || '',
                              date_cloture_vente: vacation.date_cloture_vente?.split('T')[0] || ''
                            });
                            setImagePreview(null); // Reset preview, will show existing image from editingVacation
                            setShowVacationModal(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* LOTS TAB */}
          <TabsContent value="lots" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Tous les lots ({lots.length})</h2>
              <div className="flex gap-2">
                <Select value={vacationFilter || 'all'} onValueChange={(v) => setVacationFilter(v === 'all' ? null : v)}>
                  <SelectTrigger className="w-60">
                    <SelectValue placeholder="Filtrer par vacation" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les vacations</SelectItem>
                    {vacations.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.titre_fr}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={openNewLotModal} data-testid="btn-new-lot">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau lot
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {lots
                .filter(lot => !vacationFilter || lot.vacation_id === vacationFilter)
                .map(lot => (
                  <Card key={lot.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex gap-4">
                          {/* Photo thumbnail */}
                          {lot.photos && lot.photos.length > 0 ? (
                            <img 
                              src={lot.photos[0].url?.startsWith('/') ? `${API.replace('/api', '')}${lot.photos[0].url}` : lot.photos[0].url}
                              alt={lot.titre || 'Lot'}
                              className="w-20 h-20 object-cover rounded-lg border"
                            />
                          ) : (
                            <div className="w-20 h-20 bg-gray-100 rounded-lg border flex items-center justify-center">
                              <Package className="w-8 h-8 text-gray-300" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline">Lot {lot.numero_lot}</Badge>
                              {getStatusBadge(lot.statut, 'vacation')}
                              <span className="text-xs text-gray-400">{lot.photos?.length || 0} photo(s)</span>
                            </div>
                            <h3 className="font-bold text-[#0A2540]">{lot.titre || lot.titre_fr || 'Sans titre'}</h3>
                            <p className="text-sm text-gray-500">Vacation: {lot.vacation_titre}</p>
                            <p className="text-sm text-gray-500">Vendeur: {lot.vendeur_nom} ({lot.vendeur_email})</p>
                          </div>
                        </div>
                        <div className="text-right flex flex-col items-end gap-2">
                          <div>
                            <p className="text-sm text-gray-500">Mise à prix</p>
                            <p className="text-xl font-bold">{formatPrice(lot.mise_a_prix)} €</p>
                            {lot.meilleure_offre && (
                              <p className="text-sm text-[#F0A22E]">
                                Meilleure: {formatPrice(lot.meilleure_offre)} € ({lot.nb_offres} offres)
                              </p>
                            )}
                          </div>
                          <Button size="sm" variant="outline" onClick={() => openEditLotModal(lot)}>
                            <Edit className="w-4 h-4 mr-1" />
                            Modifier
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </TabsContent>

          {/* INSCRIPTIONS TAB */}
          <TabsContent value="inscriptions" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Inscriptions acheteurs ({inscriptions.length})</h2>
              <Select value={inscriptionStatusFilter || 'all'} onValueChange={(v) => setInscriptionStatusFilter(v === 'all' ? null : v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filtrer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="recue">Reçues</SelectItem>
                  <SelectItem value="validee">Validées</SelectItem>
                  <SelectItem value="refusee">Refusées</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              {inscriptions
                .filter(i => !inscriptionStatusFilter || i.statut === inscriptionStatusFilter)
                .map(inscription => (
                  <Card key={inscription.id} className={inscription.statut === 'recue' ? 'border-orange-200' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            {getStatusBadge(inscription.statut, 'inscription')}
                            {inscription.pseudonyme && (
                              <Badge variant="outline">{inscription.pseudonyme}</Badge>
                            )}
                          </div>
                          <h3 className="font-bold text-[#0A2540]">{inscription.entreprise}</h3>
                          <p className="text-sm text-gray-500">SIRET: {inscription.siret}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {inscription.contact_email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {inscription.contact_tel}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">Vacation: {inscription.vacation_titre}</p>
                          <p className="text-xs text-gray-400 mt-1">Financement: {inscription.financement}</p>
                        </div>
                        <div className="flex flex-col gap-2">
                          {inscription.statut === 'recue' && (
                            <>
                              <Button size="sm" onClick={() => handleValidateInscription(inscription.id)}>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Valider
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => handleRefuseInscription(inscription.id)}>
                                <XCircle className="w-4 h-4 mr-1" />
                                Refuser
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedInscription(inscription);
                              setShowInscriptionModal(true);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </TabsContent>

          {/* DEMANDES VENDEUR TAB */}
          <TabsContent value="demandes" className="space-y-4">
            <h2 className="text-lg font-semibold">Demandes de compte vendeur ({demandesVendeur.length})</h2>

            <div className="space-y-3">
              {demandesVendeur.map(demande => (
                <Card key={demande.id} className={demande.statut === 'recue' ? 'border-purple-200' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusBadge(demande.statut, 'demande')}
                        </div>
                        <h3 className="font-bold text-[#0A2540]">{demande.entreprise}</h3>
                        <p className="text-sm text-gray-500">SIRET: {demande.siret}</p>
                        <p className="text-sm text-gray-500">Contact: {demande.contact_nom} ({demande.contact_fonction})</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {demande.contact_email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {demande.contact_tel}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                          Matériel: {demande.type_materiel} • {demande.nb_lots_envisages} lot(s)
                        </p>
                        {demande.message && (
                          <p className="text-sm text-gray-500 mt-2 italic">&quot;{demande.message}&quot;</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        {demande.statut === 'recue' && (
                          <Button size="sm" onClick={() => handleCreateVendorAccount(demande.id)}>
                            <UserPlus className="w-4 h-4 mr-1" />
                            Créer le compte
                          </Button>
                        )}
                        {demande.statut === 'compte_cree' && (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Compte créé
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* COMPTES TAB */}
          <TabsContent value="comptes" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Acheteurs */}
              <div>
                <h2 className="text-lg font-semibold mb-4">Acheteurs ({comptes.acheteurs.length})</h2>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {comptes.acheteurs.map(user => (
                    <Card key={user.id}>
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                          <div className="flex gap-1">
                            {user.status === 'active' ? (
                              <Button size="sm" variant="ghost" onClick={() => handleToggleAccount(user.id, 'desactiver')}>
                                <XCircle className="w-4 h-4 text-red-500" />
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" onClick={() => handleToggleAccount(user.id, 'activer')}>
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Vendeurs */}
              <div>
                <h2 className="text-lg font-semibold mb-4">Vendeurs ({comptes.vendeurs.length})</h2>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {comptes.vendeurs.map(user => (
                    <Card key={user.id}>
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{user.company || user.name}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                            <p className="text-xs text-gray-400">{user.nb_lots} lot(s)</p>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleResetPassword(user.id)}>
                              <Lock className="w-4 h-4" />
                            </Button>
                            {user.status === 'active' ? (
                              <Button size="sm" variant="ghost" onClick={() => handleToggleAccount(user.id, 'desactiver')}>
                                <XCircle className="w-4 h-4 text-red-500" />
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" onClick={() => handleToggleAccount(user.id, 'activer')}>
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ATTRIBUTIONS TAB */}
          <TabsContent value="attributions" className="space-y-4">
            <h2 className="text-lg font-semibold">Attributions ({attributions.length})</h2>

            {attributions.length === 0 ? (
              <Card className="bg-gray-50 border-dashed">
                <CardContent className="py-12 text-center">
                  <Award className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Aucune attribution enregistrée.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {attributions.map(attr => (
                  <Card key={attr.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-bold text-[#0A2540]">{attr.lot_titre}</h3>
                          <p className="text-sm text-gray-500">Attribué le {formatDate(attr.date_attribution)}</p>
                          {attr.inscription_id_retenue ? (
                            <p className="text-sm text-green-600 mt-1">
                              Acquéreur : {attr.acquereur_entreprise} ({attr.acquereur_nom})
                            </p>
                          ) : (
                            <p className="text-sm text-gray-500 mt-1">Aucune offre retenue</p>
                          )}
                          {attr.motif && (
                            <p className="text-xs text-gray-400 mt-1">Motif: {attr.motif}</p>
                          )}
                        </div>
                        <div className="text-right">
                          {attr.montant_retenu ? (
                            <p className="text-xl font-bold text-[#F0A22E]">
                              {formatPrice(attr.montant_retenu)} €
                            </p>
                          ) : (
                            <Badge variant="outline">Non vendu</Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Vacation Modal */}
      <Dialog open={showVacationModal} onOpenChange={setShowVacationModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingVacation ? 'Modifier la vacation' : 'Nouvelle vacation'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Titre *</Label>
              <Input
                value={vacationForm.titre_fr}
                onChange={(e) => setVacationForm({ ...vacationForm, titre_fr: e.target.value })}
                placeholder="Ex: Vente de matériel géotechnique - Mars 2026"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={vacationForm.description_fr}
                onChange={(e) => setVacationForm({ ...vacationForm, description_fr: e.target.value })}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Commission (%)</Label>
                <Input
                  type="number"
                  value={vacationForm.commission_pct}
                  onChange={(e) => setVacationForm({ ...vacationForm, commission_pct: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Frais dossier (€)</Label>
                <Input
                  type="number"
                  value={vacationForm.frais_dossier_eur}
                  onChange={(e) => setVacationForm({ ...vacationForm, frais_dossier_eur: parseFloat(e.target.value) })}
                />
              </div>
            </div>
            <div>
              <Label>Clôture des inscriptions</Label>
              <Input
                type="datetime-local"
                value={vacationForm.date_cloture_inscriptions}
                onChange={(e) => setVacationForm({ ...vacationForm, date_cloture_inscriptions: e.target.value })}
              />
            </div>
            <div>
              <Label>Ouverture de la vente</Label>
              <Input
                type="datetime-local"
                value={vacationForm.date_ouverture_vente}
                onChange={(e) => setVacationForm({ ...vacationForm, date_ouverture_vente: e.target.value })}
              />
            </div>
            <div>
              <Label>Clôture de la vente</Label>
              <Input
                type="datetime-local"
                value={vacationForm.date_cloture_vente}
                onChange={(e) => setVacationForm({ ...vacationForm, date_cloture_vente: e.target.value })}
              />
            </div>
            
            {/* Photo de couverture */}
            <div>
              <Label>Photo de couverture</Label>
              <div className="mt-2">
                {(imagePreview || editingVacation?.image_url) ? (
                  <div className="relative w-full h-40 rounded-lg overflow-hidden border">
                    <img 
                      src={imagePreview?.url || (editingVacation?.image_url?.startsWith('/') ? `${API.replace('/api', '')}${editingVacation.image_url}` : editingVacation?.image_url)}
                      alt="Aperçu"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview(null);
                        if (editingVacation) {
                          setEditingVacation({...editingVacation, image_url: null});
                        }
                      }}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      title="Supprimer l'image"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <ImagePlus className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Cliquez pour ajouter une image</p>
                      <p className="text-xs text-gray-400">PNG, JPG, WEBP (max. 5 Mo)</p>
                    </div>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      onChange={handleImageSelect}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVacationModal(false)}>Annuler</Button>
            <Button onClick={handleSaveVacation}>
              {editingVacation ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inscription Detail Modal */}
      <Dialog open={showInscriptionModal} onOpenChange={setShowInscriptionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Détail de l&apos;inscription</DialogTitle>
          </DialogHeader>

          {selectedInscription && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {getStatusBadge(selectedInscription.statut, 'inscription')}
                {selectedInscription.pseudonyme && (
                  <Badge variant="outline">{selectedInscription.pseudonyme}</Badge>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Entreprise</p>
                  <p className="font-medium">{selectedInscription.entreprise}</p>
                </div>
                <div>
                  <p className="text-gray-500">SIRET</p>
                  <p className="font-medium">{selectedInscription.siret}</p>
                </div>
                <div>
                  <p className="text-gray-500">Contact</p>
                  <p className="font-medium">{selectedInscription.contact_nom}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium">{selectedInscription.contact_email}</p>
                </div>
                <div>
                  <p className="text-gray-500">Téléphone</p>
                  <p className="font-medium">{selectedInscription.contact_tel}</p>
                </div>
                <div>
                  <p className="text-gray-500">Pays</p>
                  <p className="font-medium">{selectedInscription.pays}</p>
                </div>
                <div>
                  <p className="text-gray-500">Financement</p>
                  <p className="font-medium">{selectedInscription.financement}</p>
                </div>
                <div>
                  <p className="text-gray-500">Visite effectuée</p>
                  <p className="font-medium">{selectedInscription.visite_effectuee ? 'Oui' : 'Non'}</p>
                </div>
              </div>

              {selectedInscription.projet && (
                <div>
                  <p className="text-gray-500 text-sm">Projet</p>
                  <p className="text-sm">{selectedInscription.projet}</p>
                </div>
              )}

              {selectedInscription.note_admin && (
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500 text-sm">Note admin</p>
                  <p className="text-sm">{selectedInscription.note_admin}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInscriptionModal(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lot Modal - Create/Edit */}
      <Dialog open={showLotModal} onOpenChange={setShowLotModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLot ? 'Modifier le lot' : 'Nouveau lot'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Vacation selection */}
            <div>
              <Label>Vacation *</Label>
              <Select 
                value={lotForm.vacation_id} 
                onValueChange={(v) => setLotForm({ ...lotForm, vacation_id: v })}
                disabled={!!editingLot}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une vacation" />
                </SelectTrigger>
                <SelectContent>
                  {vacations.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.titre_fr}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div>
              <Label>Titre *</Label>
              <Input
                value={lotForm.titre}
                onChange={(e) => setLotForm({ ...lotForm, titre: e.target.value })}
                placeholder="Ex: Foreuse hydraulique CMV MK-800"
              />
            </div>

            {/* Description */}
            <div>
              <Label>Description</Label>
              <Textarea
                value={lotForm.description_vendeur_fr}
                onChange={(e) => setLotForm({ ...lotForm, description_vendeur_fr: e.target.value })}
                rows={3}
                placeholder="Description détaillée du matériel..."
              />
            </div>

            {/* Brand/Model row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Marque</Label>
                <Input
                  value={lotForm.marque}
                  onChange={(e) => setLotForm({ ...lotForm, marque: e.target.value })}
                  placeholder="Ex: CMV, Atlas Copco..."
                />
              </div>
              <div>
                <Label>Modèle</Label>
                <Input
                  value={lotForm.modele}
                  onChange={(e) => setLotForm({ ...lotForm, modele: e.target.value })}
                  placeholder="Ex: MK-800"
                />
              </div>
            </div>

            {/* Year/Hours row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Année</Label>
                <Input
                  type="number"
                  value={lotForm.annee}
                  onChange={(e) => setLotForm({ ...lotForm, annee: e.target.value })}
                  placeholder="Ex: 2018"
                />
              </div>
              <div>
                <Label>Heures d&apos;utilisation</Label>
                <Input
                  type="number"
                  value={lotForm.heures_utilisation}
                  onChange={(e) => setLotForm({ ...lotForm, heures_utilisation: e.target.value })}
                  placeholder="Ex: 3500"
                />
              </div>
            </div>

            {/* Condition */}
            <div>
              <Label>État général</Label>
              <Select 
                value={lotForm.etat_general} 
                onValueChange={(v) => setLotForm({ ...lotForm, etat_general: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="neuf">Neuf</SelectItem>
                  <SelectItem value="excellent">Excellent</SelectItem>
                  <SelectItem value="tres_bon">Très bon</SelectItem>
                  <SelectItem value="bon">Bon</SelectItem>
                  <SelectItem value="correct">Correct</SelectItem>
                  <SelectItem value="a_reviser">À réviser</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Location */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Commune</Label>
                <Input
                  value={lotForm.commune}
                  onChange={(e) => setLotForm({ ...lotForm, commune: e.target.value })}
                  placeholder="Ex: Lyon"
                />
              </div>
              <div>
                <Label>Département</Label>
                <Input
                  value={lotForm.departement}
                  onChange={(e) => setLotForm({ ...lotForm, departement: e.target.value })}
                  placeholder="Ex: 69"
                />
              </div>
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Mise à prix (€)</Label>
                <Input
                  type="number"
                  value={lotForm.mise_a_prix}
                  onChange={(e) => setLotForm({ ...lotForm, mise_a_prix: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Pas d&apos;enchère (€)</Label>
                <Input
                  type="number"
                  value={lotForm.pas_enchere}
                  onChange={(e) => setLotForm({ ...lotForm, pas_enchere: parseFloat(e.target.value) || 100 })}
                />
              </div>
            </div>

            {/* Photos section - only for editing */}
            {editingLot && (
              <div>
                <Label>Photos du lot</Label>
                <div className="mt-2 space-y-3">
                  {/* Existing photos */}
                  {lotPhotos.length > 0 && (
                    <div className="grid grid-cols-4 gap-2">
                      {lotPhotos.map((photo, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={photo.url?.startsWith('/') ? `${API.replace('/api', '')}${photo.url}` : photo.url}
                            alt={photo.alt_fr || 'Photo'}
                            className="w-full h-20 object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => handleDeleteLotPhoto(index)}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          {photo.is_main && (
                            <span className="absolute bottom-1 left-1 text-xs bg-green-500 text-white px-1 rounded">
                              Principale
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload new photo */}
                  <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <div className="flex flex-col items-center justify-center py-4">
                      {uploadingLotPhoto ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#F0A22E]"></div>
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-gray-400 mb-1" />
                          <p className="text-sm text-gray-500">Cliquez pour ajouter une photo</p>
                          <p className="text-xs text-gray-400">PNG, JPG, WEBP (max. 10 Mo)</p>
                        </>
                      )}
                    </div>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      onChange={handleLotPhotoUpload}
                      disabled={uploadingLotPhoto}
                    />
                  </label>
                </div>
              </div>
            )}

            {!editingLot && (
              <p className="text-sm text-gray-500 italic">
                💡 Vous pourrez ajouter des photos après avoir créé le lot.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLotModal(false)}>Annuler</Button>
            <Button onClick={handleSaveLot}>
              {editingLot ? 'Enregistrer' : 'Créer le lot'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
